import { supabase } from '../utils/supabase';
import { createNotification } from './notificationService';
import { assignEmailToGuest, countPlayerClubGames } from './guestService';

/**
 * Guest Account Linking Service
 * Handles all guest account linking and merging operations
 */

/**
 * Normalize email address for consistent matching
 * @param {string} email - Email address
 * @returns {string} - Normalized email
 */
export const normalizeEmail = (email) => {
  if (!email) return null;
  // Lowercase, trim, remove +tags (e.g., user+tag@example.com -> user@example.com)
  return email.toLowerCase().trim().replace(/\+\w+@/, '@');
};

/**
 * Check for guest account matches by email
 * @param {string} email - User email
 * @returns {Promise<Array>} - Array of matching guest accounts
 */
export const checkForGuestAccountMatch = async (email) => {
  try {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return [];

    const { data, error } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        guest_invited_email,
        created_at,
        players_club!inner (
          club_id,
          clubs (
            id,
            name
          )
        )
      `)
      .eq('is_guest', true)
      .is('merged_at', null)
      .or(`guest_invited_email.ilike.%${normalizedEmail}%`);

    if (error) throw error;

    // Filter by normalized email match
    return (data || []).filter(guest => 
      normalizeEmail(guest.guest_invited_email) === normalizedEmail
    );
  } catch (error) {
    console.error('[GuestLinkingService] Error checking for guest matches:', error);
    throw error;
  }
};

/**
 * Get all guest accounts for a user (pending and declined)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { pending, declined }
 */
export const getAllGuestAccountsForUser = async (userId) => {
  try {
    // Get user email
    const { data: user, error: userError } = await supabase
      .from('players')
      .select('email')
      .eq('id', userId)
      .single();

    if (userError) throw userError;
    if (!user?.email) return { pending: [], declined: [] };

    const normalizedEmail = normalizeEmail(user.email);

    // Get pending guests (not merged, not declined)
    const { data: pending, error: pendingError } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        guest_invited_email,
        created_at,
        players_club (
          club_id,
          is_active,
          clubs (
            id,
            name
          )
        )
      `)
      .eq('is_guest', true)
      .is('merged_at', null)
      .is('guest_merge_declined_at', null)
      .ilike('guest_invited_email', `%${normalizedEmail}%`);

    if (pendingError) throw pendingError;

    // Get declined guests (declined but not merged)
    const { data: declined, error: declinedError } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        guest_invited_email,
        guest_merge_declined_at,
        created_at,
        players_club (
          club_id,
          is_active,
          clubs (
            id,
            name
          )
        )
      `)
      .eq('is_guest', true)
      .is('merged_at', null)
      .not('guest_merge_declined_at', 'is', null);

    if (declinedError) throw declinedError;

    // Filter by normalized email
    const normalizedPending = (pending || []).filter(guest =>
      normalizeEmail(guest.guest_invited_email) === normalizedEmail
    );
    const normalizedDeclined = (declined || []).filter(guest =>
      normalizeEmail(guest.guest_invited_email) === normalizedEmail
    );

    return {
      pending: normalizedPending,
      declined: normalizedDeclined
    };
  } catch (error) {
    console.error('[GuestLinkingService] Error getting guest accounts:', error);
    throw error;
  }
};

/**
 * Add display stats (games, sessions, date range, club names) for merge UI cards.
 * @param {Array} guests - Guest rows from getAllGuestAccountsForUser
 * @returns {Promise<Array>}
 */
export const enrichGuestAccountsForDisplay = async (guests) => {
  if (!guests?.length) return [];

  return Promise.all(
    guests.map(async (guest) => {
      const { count: gameCount } = await supabase
        .from('players_session_games')
        .select('*', { count: 'exact', head: true })
        .eq('player_id', guest.id);

      const { data: firstGameRow } = await supabase
        .from('players_session_games')
        .select('created_at')
        .eq('player_id', guest.id)
        .order('created_at', { ascending: true })
        .limit(1);

      const { data: lastGameRow } = await supabase
        .from('players_session_games')
        .select('created_at')
        .eq('player_id', guest.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const { data: sessionGames } = await supabase
        .from('players_session_games')
        .select('session_games!inner(session_id)')
        .eq('player_id', guest.id);

      const uniqueSessions = new Set(
        sessionGames?.map((sg) => sg.session_games?.session_id).filter(Boolean) || []
      );

      const clubNames = (guest.players_club || [])
        .map((pc) => pc.clubs?.name)
        .filter(Boolean);

      return {
        ...guest,
        game_count: gameCount || 0,
        first_game_date: firstGameRow?.[0]?.created_at || guest.created_at,
        last_game_date: lastGameRow?.[0]?.created_at || guest.created_at,
        total_sessions: uniqueSessions.size,
        clubs_label: clubNames.join(', '),
      };
    })
  );
};

/**
 * Get guest account preview data before merge
 * @param {string} guestId - Guest ID
 * @returns {Promise<Object>} - Preview data with counts
 */
export const getGuestAccountPreview = async (guestId) => {
  try {
    // Get guest basic info
    const { data: guest, error: guestError } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        created_at,
        players_club (
          club_id,
          club_role,
          is_active,
          clubs (
            id,
            name
          )
        )
      `)
      .eq('id', guestId)
      .eq('is_guest', true)
      .single();

    if (guestError) throw guestError;
    if (!guest) throw new Error('Guest account not found');

    // Count games
    const { count: gameCount, error: gameError } = await supabase
      .from('players_session_games')
      .select('*', { count: 'exact', head: true })
      .eq('player_id', guestId);

    if (gameError) throw gameError;

    // Count ELO records
    const { count: eloCount, error: eloError } = await supabase
      .from('elo_history')
      .select('*', { count: 'exact', head: true })
      .eq('player_id', guestId);

    if (eloError) throw eloError;

    // Count partnerships
    const { count: partnershipCount, error: partnershipError } = await supabase
      .from('partnerships')
      .select('*', { count: 'exact', head: true })
      .or(`player1_id.eq.${guestId},player2_id.eq.${guestId}`);

    if (partnershipError) throw partnershipError;

    // Active club memberships only (matches merge-and-join behaviour)
    const clubs = (guest.players_club || [])
      .filter((pc) => pc.is_active !== false)
      .map((pc) => ({
        id: pc.club_id,
        name: pc.clubs?.name || 'Unknown Club',
        role: pc.club_role,
      }));

    return {
      guest: {
        id: guest.id,
        name: `${guest.first_name} ${guest.last_name || ''}`.trim(),
        created_at: guest.created_at
      },
      counts: {
        games: gameCount || 0,
        elo_records: eloCount || 0,
        partnerships: partnershipCount || 0,
        clubs: clubs.length
      },
      clubs
    };
  } catch (error) {
    console.error('[GuestLinkingService] Error getting guest preview:', error);
    throw error;
  }
};

/**
 * Validate merge eligibility
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { eligible, reason }
 */
export const validateMergeEligibility = async (guestId, userId) => {
  try {
    // Get guest
    const { data: guest, error: guestError } = await supabase
      .from('players')
      .select('id, is_guest, merged_at, guest_merge_declined_at, guest_invited_email')
      .eq('id', guestId)
      .single();

    if (guestError) throw guestError;
    if (!guest) {
      return { eligible: false, reason: 'Guest account not found' };
    }

    // Check if guest
    if (!guest.is_guest) {
      return { eligible: false, reason: 'Player is not a guest account' };
    }

    // Check if already merged
    if (guest.merged_at) {
      return { eligible: false, reason: 'Guest account already merged' };
    }

    // Get user email
    const { data: user, error: userError } = await supabase
      .from('players')
      .select('id, email, is_guest')
      .eq('id', userId)
      .single();

    if (userError) throw userError;
    if (!user) {
      return { eligible: false, reason: 'User account not found' };
    }

    // Check user is not a guest
    if (user.is_guest) {
      return { eligible: false, reason: 'Cannot merge: target is a guest account' };
    }

    // Check email match (if both are set)
    if (user.email && guest.guest_invited_email) {
      const normalizedUserEmail = normalizeEmail(user.email);
      const normalizedGuestEmail = normalizeEmail(guest.guest_invited_email);
      
      if (normalizedUserEmail !== normalizedGuestEmail) {
        return { eligible: false, reason: 'Email mismatch' };
      }
    }

    // Check if declined (can still merge if user wants to reconsider)
    if (guest.guest_merge_declined_at) {
      // Allow reconsideration
      return { eligible: true, reason: 'Previously declined, but eligible for reconsideration' };
    }

    return { eligible: true, reason: 'Eligible for merge' };
  } catch (error) {
    console.error('[GuestLinkingService] Error validating merge eligibility:', error);
    throw error;
  }
};

/**
 * Merge a guest into the user and join them to the guest's club(s).
 * Uses the club-join RPC when clubs exist so the user becomes an active member (not guest role only).
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @param {string[]} [clubIds] - Club IDs from the guest's memberships (active clubs only)
 * @returns {Promise<Object>} - Merge result
 */
export const mergeGuestAccountAndJoinClubs = async (guestId, userId, clubIds = []) => {
  const activeClubIds = [...new Set((clubIds || []).filter(Boolean))];

  if (activeClubIds.length === 0) {
    return mergeGuestAccountToUser(guestId, userId);
  }

  // validate_and_merge_guest_for_club_join runs merge_guest_account_to_user (all clubs)
  // then promotes this club to member. Use the first listed club as primary.
  const primaryClubId = activeClubIds[0];
  const result = await linkGuestAccountBeforeClubJoin(guestId, userId, primaryClubId);

  const merged =
    result?.status === 'merged' ||
    result?.club_joined === true;

  if (!merged) {
    if (result?.status === 'already_merged') {
      return result;
    }
    throw new Error(result?.message || 'Failed to merge guest account and join club');
  }

  return {
    ...result,
    status: 'merged',
    clubs_affected: result.clubs_affected ?? activeClubIds.length,
    clubs_joined: activeClubIds,
  };
};

/**
 * Merge guest account to user account
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Merge result
 */
export const mergeGuestAccountToUser = async (guestId, userId) => {
  try {
    // Validate eligibility first
    const validation = await validateMergeEligibility(guestId, userId);
    if (!validation.eligible) {
      throw new Error(validation.reason);
    }

    // Call PostgreSQL function
    const { data, error } = await supabase.rpc('merge_guest_account_to_user', {
      p_guest_id: guestId,
      p_user_id: userId
    });

    if (error) throw error;

    // If already merged, return that status
    if (data?.status === 'already_merged') {
      return data;
    }

    // If merge successful, mark related notifications as read
    if (data?.status === 'merged') {
      // Mark guest merge notifications as read
      const { data: notifications } = await supabase
        .from('notifications')
        .select('id')
        .eq('user_id', userId)
        .eq('type', 'guest_account_merge')
        .eq('data->>guest_id', guestId)
        .eq('is_read', false);

      if (notifications && notifications.length > 0) {
        await Promise.all(
          notifications.map(n => 
            supabase
              .from('notifications')
              .update({ is_read: true, read_at: new Date().toISOString() })
              .eq('id', n.id)
          )
        );
      }
    }

    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error merging guest account:', error);
    throw error;
  }
};

/**
 * Merge all guest accounts to user account (bulk)
 * @param {string} userId - User ID
 * @param {Array<string>} guestIds - Array of guest IDs
 * @returns {Promise<Object>} - Bulk merge result
 */
export const mergeAllGuestAccountsToUser = async (userId, guestIds) => {
  try {
    const { data, error } = await supabase.rpc('merge_all_guest_accounts_to_user', {
      p_user_id: userId,
      p_guest_ids: guestIds
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error bulk merging guest accounts:', error);
    throw error;
  }
};

/**
 * Decline guest account merge
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Updated guest
 */
export const declineGuestAccountMerge = async (guestId, userId) => {
  try {
    // Update guest to mark as declined
    const { data, error } = await supabase
      .from('players')
      .update({
        guest_merge_declined_at: new Date().toISOString(),
        guest_merge_declined_by: userId
      })
      .eq('id', guestId)
      .eq('is_guest', true)
      .is('merged_at', null)
      .select()
      .single();

    if (error) throw error;

    // Record in merge history
    await supabase
      .from('guest_account_merge_history')
      .insert({
        guest_id: guestId,
        user_id: userId,
        action: 'declined',
        performed_by: userId,
        details: { declined_at: new Date().toISOString() }
      });

    // Mark related notifications as read
    const { data: notifications } = await supabase
      .from('notifications')
      .select('id')
      .eq('user_id', userId)
      .eq('type', 'guest_account_merge')
      .eq('data->>guest_id', guestId)
      .eq('is_read', false);

    if (notifications && notifications.length > 0) {
      await Promise.all(
        notifications.map(n =>
          supabase
            .from('notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('id', n.id)
        )
      );
    }

    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error declining merge:', error);
    throw error;
  }
};

/**
 * Reconsider a previously declined guest account merge
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Updated guest
 */
export const reconsiderGuestAccountMerge = async (guestId, userId) => {
  try {
    // Clear declined flags
    const { data, error } = await supabase
      .from('players')
      .update({
        guest_merge_declined_at: null,
        guest_merge_declined_by: null
      })
      .eq('id', guestId)
      .eq('is_guest', true)
      .is('merged_at', null)
      .select()
      .single();

    if (error) throw error;

    // Record in merge history
    await supabase
      .from('guest_account_merge_history')
      .insert({
        guest_id: guestId,
        user_id: userId,
        action: 'reconsidered',
        performed_by: userId,
        details: { reconsidered_at: new Date().toISOString() }
      });

    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error reconsidering merge:', error);
    throw error;
  }
};

/**
 * Sanitized guest name hints for the join-gate No path.
 * RPC only returns first name + last initial and a coarse activity label.
 * @param {string} clubId
 * @returns {Promise<Array<{display_name: string, activity_label: string}>>}
 */
export const suggestGuestNameMatchesForJoin = async (clubId) => {
  if (!clubId) return [];

  try {
    const { data, error } = await supabase.rpc('suggest_guest_name_matches_for_join', {
      p_club_id: clubId,
    });

    if (error) throw error;

    return (data || [])
      .map((row) => ({
        display_name: row.display_name || 'Guest',
        activity_label: row.activity_label || 'Listed as a guest here',
      }))
      .slice(0, 3);
  } catch (error) {
    console.error('[GuestLinkingService] Error suggesting guest name matches:', error);
    throw error;
  }
};

/**
 * Check for guest accounts at a specific club
 * @param {string} userId - User ID
 * @param {string} clubId - Club ID
 * @returns {Promise<Array>} - Array of potential guest matches
 */
export const checkForGuestAccountsAtClub = async (userId, clubId) => {
  try {
    // Get user email
    const { data: user, error: userError } = await supabase
      .from('players')
      .select('email')
      .eq('id', userId)
      .single();

    if (userError) throw userError;
    if (!user?.email) return [];

    const normalizedEmail = normalizeEmail(user.email);

    // Find guests at this club with matching email
    const { data, error } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        guest_invited_email,
        created_at,
        players_club!inner (
          club_id,
          club_role
        )
      `)
      .eq('is_guest', true)
      .eq('players_club.club_id', clubId)
      .is('merged_at', null)
      .ilike('guest_invited_email', `%${normalizedEmail}%`);

    if (error) throw error;

    // Filter by normalized email and get game counts
    const guests = (data || []).filter(guest =>
      normalizeEmail(guest.guest_invited_email) === normalizedEmail
    );

    // Get game counts, date ranges, and stats for each guest
    const guestsWithDetails = await Promise.all(
      guests.map(async (guest) => {
        // Get game count
        const { count: gameCount } = await supabase
          .from('players_session_games')
          .select('*', { count: 'exact', head: true })
          .eq('player_id', guest.id);

        // Get date range
        const { data: games } = await supabase
          .from('players_session_games')
          .select('created_at')
          .eq('player_id', guest.id)
          .order('created_at', { ascending: true })
          .limit(1);

        const { data: lastGame } = await supabase
          .from('players_session_games')
          .select('created_at')
          .eq('player_id', guest.id)
          .order('created_at', { ascending: false })
          .limit(1);

        // Get ELO ratings for this club
        const { data: eloRatings } = await supabase
          .from('player_club_elo_ratings')
          .select('mixed_elo_rating, levels_elo_rating, doubles_elo_rating, singles_elo_rating')
          .eq('player_id', guest.id)
          .eq('club_id', clubId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Get unique session count
        const { data: sessionGames } = await supabase
          .from('players_session_games')
          .select('session_games!inner(session_id)')
          .eq('player_id', guest.id);

        const uniqueSessions = new Set(
          sessionGames?.map(sg => sg.session_games?.session_id).filter(Boolean) || []
        );

        // Get last game details from player_game_history view
        const { data: lastGameDetails } = await supabase
          .from('player_game_history')
          .select(`
            created_at,
            result,
            team_1_score,
            team_2_score,
            session_name,
            team_number,
            game_id
          `)
          .eq('player_id', guest.id)
          .eq('club_id', clubId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        let lastGameInfo = null;
        if (lastGameDetails && lastGameDetails.game_id) {
          const isWin = lastGameDetails.result === 'win';
          const score = lastGameDetails.team_1_score !== null && lastGameDetails.team_2_score !== null
            ? `${lastGameDetails.team_1_score}-${lastGameDetails.team_2_score}`
            : null;
          
          // Get all players in this game to find teammates and opponents
          const { data: gamePlayers } = await supabase
            .from('players_session_games')
            .select(`
              player_id,
              team_number,
              players:player_id(
                id,
                first_name,
                last_name
              )
            `)
            .eq('session_game_id', lastGameDetails.game_id);

          const guestTeamNumber = lastGameDetails.team_number;
          const teammates = [];
          const opponents = [];

          if (gamePlayers) {
            gamePlayers.forEach((gp) => {
              if (!gp.players || gp.player_id === guest.id) return; // Skip the guest themselves
              
              const firstName = gp.players.first_name || '';
              
              if (gp.team_number === guestTeamNumber) {
                teammates.push(firstName);
              } else {
                opponents.push(firstName);
              }
            });
          }
          
          lastGameInfo = {
            date: lastGameDetails.created_at,
            result: isWin ? 'Win' : 'Loss',
            score: score,
            sessionName: lastGameDetails.session_name || null,
            teammates: teammates,
            opponents: opponents
          };
        }

        return {
          ...guest,
          game_count: gameCount || 0,
          first_game_date: games?.[0]?.created_at || guest.created_at,
          last_game_date: lastGame?.[0]?.created_at || guest.created_at,
          mixed_elo: eloRatings?.mixed_elo_rating || null,
          levels_elo: eloRatings?.levels_elo_rating || null,
          doubles_elo: eloRatings?.doubles_elo_rating || null,
          singles_elo: eloRatings?.singles_elo_rating || null,
          total_sessions: uniqueSessions.size,
          last_game: lastGameInfo
        };
      })
    );

    return guestsWithDetails;
  } catch (error) {
    console.error('[GuestLinkingService] Error checking for guests at club:', error);
    throw error;
  }
};

/**
 * Get guest accounts for club selection UI
 * @param {string} clubId - Club ID
 * @returns {Promise<Array>} - List of guests with details
 */
export const getGuestAccountsForClubSelection = async (clubId) => {
  try {
    const { data, error } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        created_at,
        players_club!inner (
          club_id,
          club_role
        )
      `)
      .eq('is_guest', true)
      .eq('players_club.club_id', clubId)
      .eq('players_club.is_active', true)
      .is('merged_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get game counts and date ranges
    const guestsWithDetails = await Promise.all(
      (data || []).map(async (guest) => {
        // Get game count
        const { count: gameCount } = await supabase
          .from('players_session_games')
          .select('*', { count: 'exact', head: true })
          .eq('player_id', guest.id);

        // Get date range
        const { data: games } = await supabase
          .from('players_session_games')
          .select('created_at')
          .eq('player_id', guest.id)
          .order('created_at', { ascending: true })
          .limit(1);

        const { data: lastGame } = await supabase
          .from('players_session_games')
          .select('created_at')
          .eq('player_id', guest.id)
          .order('created_at', { ascending: false })
          .limit(1);

        return {
          id: guest.id,
          name: `${guest.first_name} ${guest.last_name || ''}`.trim(),
          game_count: gameCount || 0,
          first_game_date: games?.[0]?.created_at || guest.created_at,
          last_game_date: lastGame?.[0]?.created_at || guest.created_at
        };
      })
    );

    return guestsWithDetails;
  } catch (error) {
    console.error('[GuestLinkingService] Error getting guests for selection:', error);
    throw error;
  }
};

/**
 * Validate club join eligibility (enforce identity gate)
 * @param {string} userId - User ID
 * @param {string} clubId - Club ID
 * @param {boolean} hasPlayedAsGuest - Whether user confirmed playing as guest
 * @returns {Promise<Object>} - { eligible, reason, requiresMerge }
 */
export const validateClubJoinEligibility = async (userId, clubId, hasPlayedAsGuest) => {
  try {
    // If user says they haven't played as guest, allow join
    if (!hasPlayedAsGuest) {
      return { eligible: true, reason: 'No guest play confirmed', requiresMerge: false };
    }

    // Check if user already has a merged guest at this club
    const { data: existingMembership } = await supabase
      .from('players_club')
      .select('id')
      .eq('player_id', userId)
      .eq('club_id', clubId)
      .eq('is_active', true)
      .single();

    if (existingMembership) {
      return { eligible: true, reason: 'Already a member', requiresMerge: false };
    }

    // Check if there are any unmerged guests at this club that could match
    const { data: user } = await supabase
      .from('players')
      .select('email')
      .eq('id', userId)
      .single();

    if (!user?.email) {
      return { eligible: false, reason: 'User email not found', requiresMerge: true };
    }

    const guests = await checkForGuestAccountsAtClub(userId, clubId);

    if (guests.length === 0) {
      // No guest found - user needs to ask admin to create/locate guest record
      return {
        eligible: false,
        reason: 'No guest account found. Please ask a club admin to create or locate your guest record.',
        requiresMerge: true
      };
    }

    // Guest(s) found - merge required
    return {
      eligible: false,
      reason: 'Guest account(s) found. Please link your guest account before joining.',
      requiresMerge: true,
      guestAccounts: guests
    };
  } catch (error) {
    console.error('[GuestLinkingService] Error validating club join eligibility:', error);
    throw error;
  }
};

/**
 * Link guest account before club join (merge + join in one transaction)
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} - Merge and join result
 */
export const linkGuestAccountBeforeClubJoin = async (guestId, userId, clubId) => {
  try {
    // Call PostgreSQL function that handles merge + join atomically
    const { data, error } = await supabase.rpc('validate_and_merge_guest_for_club_join', {
      p_user_id: userId,
      p_club_id: clubId,
      p_guest_id: guestId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error linking guest before club join:', error);
    throw error;
  }
};

/**
 * Start fresh without merging guest data (archives guest, creates new membership)
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} - Start fresh result
 */
export const startFreshWithoutGuestMerge = async (guestId, userId, clubId) => {
  try {
    const { data, error } = await supabase.rpc('start_fresh_without_guest_merge', {
      p_user_id: userId,
      p_club_id: clubId,
      p_guest_id: guestId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error starting fresh:', error);
    throw error;
  }
};

/**
 * Decline guest merge and report incorrect assignment
 * @param {string} guestId - Guest ID
 * @param {string} userId - User ID
 * @param {string} clubId - Club ID
 * @param {string} userMessage - Optional message from user explaining why it's incorrect
 * @returns {Promise<Object>} - Decline result
 */
export const declineGuestMergeWithReport = async (guestId, userId, clubId, userMessage = null) => {
  try {
    const { data, error } = await supabase.rpc('decline_guest_merge_with_report', {
      p_user_id: userId,
      p_club_id: clubId,
      p_guest_id: guestId,
      p_user_message: userMessage || null
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error declining guest merge:', error);
    throw error;
  }
};

/**
 * Create email assignment request for club admins
 * @param {string} userId - User ID
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} - Request creation result
 */
export const createEmailAssignmentRequest = async (userId, clubId) => {
  try {
    const { data, error } = await supabase.rpc('create_guest_email_assignment_request', {
      p_user_id: userId,
      p_club_id: clubId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[GuestLinkingService] Error creating email assignment request:', error);
    throw error;
  }
};

/**
 * Get pending email assignment requests for a club
 * @param {string} clubId - Club ID
 * @returns {Promise<Array>} - Array of pending requests
 */
export const getPendingEmailAssignmentRequests = async (clubId) => {
  try {
    const { data, error } = await supabase.rpc('get_pending_email_assignment_requests', {
      p_club_id: clubId
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('[GuestLinkingService] Error getting pending email assignment requests:', error);
    throw error;
  }
};

/**
 * Club ids where the current user has an outstanding account-link request.
 * Uses a SECURITY DEFINER RPC because the notifications are addressed to admins.
 * @returns {Promise<string[]>}
 */
export const getMyPendingEmailAssignmentClubIds = async () => {
  const { data, error } = await supabase.rpc('get_my_pending_email_assignment_requests');
  if (error) throw error;
  return (data || []).map((row) => row.club_id).filter(Boolean);
};

/**
 * Check if user has a pending email assignment request for a club
 * @param {string} userId - User ID (requester) — unused; caller is taken from auth
 * @param {string} clubId - Club ID
 * @returns {Promise<boolean>} - True if user has a pending request
 */
export const hasPendingEmailAssignmentRequest = async (userId, clubId) => {
  if (!clubId) return false;
  try {
    const clubIds = await getMyPendingEmailAssignmentClubIds();
    return clubIds.includes(clubId);
  } catch (error) {
    console.error('[GuestLinkingService] Error checking pending email assignment request:', error);
    return false;
  }
};

/**
 * Get pending account link requests for the current admin (Player Management view).
 * @param {string} clubId - Club ID
 * @param {string} adminUserId - Current admin's player ID
 * @returns {Promise<Array>}
 */
export const getPendingAccountLinkRequestsForAdmin = async (clubId, adminUserId) => {
  try {
    const { data: notifications, error } = await supabase
      .from('notifications')
      .select('id, data, created_at')
      .eq('user_id', adminUserId)
      .eq('type', 'guest_email_assignment_request')
      .eq('is_archived', false)
      .eq('data->>club_id', clubId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!notifications?.length) return [];

    const seenRequesters = new Set();
    const requests = [];

    for (const notification of notifications) {
      const requestUserId = notification.data?.user_id;
      const userEmail = notification.data?.user_email;

      if (!requestUserId || !userEmail || seenRequesters.has(requestUserId)) {
        continue;
      }

      // Skip if requester is already an active member
      const { data: membership } = await supabase
        .from('players_club')
        .select('id')
        .eq('player_id', requestUserId)
        .eq('club_id', clubId)
        .eq('is_active', true)
        .maybeSingle();

      if (membership) continue;

      // Skip if an admin already approved or rejected this player via join request flow
      const { data: latestJoinRequest } = await supabase
        .from('club_join_requests')
        .select('status')
        .eq('player_id', requestUserId)
        .eq('club_id', clubId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (latestJoinRequest?.status === 'approved' || latestJoinRequest?.status === 'rejected') {
        continue;
      }

      // Skip if email is already assigned to a guest at this club
      const normalizedEmail = normalizeEmail(userEmail);
      const { data: guestsWithEmail } = await supabase
        .from('players')
        .select(`
          guest_invited_email,
          players_club!inner (club_id, is_active)
        `)
        .eq('is_guest', true)
        .is('merged_at', null)
        .eq('players_club.club_id', clubId)
        .eq('players_club.is_active', true)
        .not('guest_invited_email', 'is', null);

      const alreadyAssigned = (guestsWithEmail || []).some(
        (guest) => normalizeEmail(guest.guest_invited_email) === normalizedEmail
      );

      if (alreadyAssigned) continue;

      seenRequesters.add(requestUserId);

      const { data: playerProfile } = await supabase
        .from('players')
        .select('first_name, last_name, gender, avatar_url')
        .eq('id', requestUserId)
        .maybeSingle();

      requests.push({
        notification_id: notification.id,
        user_id: requestUserId,
        user_name: notification.data?.user_name || 'Unknown player',
        user_first_name: playerProfile?.first_name || '',
        user_last_name: playerProfile?.last_name || '',
        user_gender: playerProfile?.gender || null,
        user_avatar_url: playerProfile?.avatar_url || null,
        user_email: userEmail,
        created_at: notification.created_at,
        guest_accounts: notification.data?.guest_accounts || [],
      });
    }

    return requests;
  } catch (error) {
    console.error('[GuestLinkingService] Error getting account link requests for admin:', error);
    throw error;
  }
};

/**
 * Mark the admin's account-link request notification as read.
 */
export const markAccountLinkRequestNotificationsRead = async (clubId, requestUserId, adminUserId) => {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
      is_archived: true,
    })
    .eq('user_id', adminUserId)
    .eq('type', 'guest_email_assignment_request')
    .eq('data->>club_id', clubId)
    .eq('data->>user_id', requestUserId)
    .eq('is_archived', false);

  if (error) throw error;
};

/**
 * Approve the player as a member without linking a guest account.
 */
export const approveMemberWithoutGuestLink = async (clubId, playerId, approvedBy, adminUserId) => {
  const { data: user, error: userError } = await supabase
    .from('players')
    .select('email, first_name, last_name')
    .eq('id', playerId)
    .eq('is_guest', false)
    .maybeSingle();

  if (userError) throw userError;

  if (user?.email) {
    const normalizedEmail = normalizeEmail(user.email);
    const { data: pendingGuests, error: guestsError } = await supabase
      .from('players')
      .select(`
        guest_invited_email,
        players_club!inner (club_id, is_active)
      `)
      .eq('is_guest', true)
      .is('merged_at', null)
      .eq('players_club.club_id', clubId)
      .eq('players_club.is_active', true)
      .not('guest_invited_email', 'is', null);

    if (guestsError) throw guestsError;

    const hasPendingGuest = (pendingGuests || []).some(
      (guest) => normalizeEmail(guest.guest_invited_email) === normalizedEmail
    );

    if (hasPendingGuest) {
      const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'This player';
      throw new Error(
        `${userName} has a guest account awaiting account link at this club. Complete the link flow instead of approving without a guest player.`
      );
    }
  }

  const { data, error } = await supabase.rpc('approve_member_without_guest_link', {
    p_player_id: playerId,
    p_club_id: clubId,
    p_approved_by: approvedBy,
  });

  if (error) throw error;
  if (data?.status === 'error') {
    throw new Error(data.error || 'Failed to approve join request');
  }

  await markAccountLinkRequestNotificationsRead(clubId, playerId, adminUserId);
  return data;
};

/**
 * Reject an account-link request using the standard join-request rejection flow.
 */
export const rejectAccountLinkViaJoinRequest = async (
  clubId,
  playerId,
  rejectedBy,
  adminUserId,
  rejectionData = {}
) => {
  const cooldownDays =
    rejectionData.cooldownDays !== undefined && rejectionData.cooldownDays !== null
      ? rejectionData.cooldownDays
      : 7;

  const { data, error } = await supabase.rpc('reject_account_link_request', {
    p_player_id: playerId,
    p_club_id: clubId,
    p_rejected_by: rejectedBy,
    p_rejection_message: rejectionData.rejectionMessage || null,
    p_cooldown_days: cooldownDays,
    p_block_future: rejectionData.blockFuture || false,
  });

  if (error) throw error;
  if (data?.status === 'error') {
    throw new Error(data.error || 'Failed to reject join request');
  }

  await markAccountLinkRequestNotificationsRead(clubId, playerId, adminUserId);
  return data;
};

/**
 * Assign email to guest and trigger merge notification
 * @param {string} guestId - Guest ID
 * @param {string} email - Email address
 * @param {string} assignedBy - Admin user ID
 * @param {string} clubId - Optional club ID for club-specific validation
 * @returns {Promise<Object>} - Assignment result
 */
export const assignEmailToGuestAndNotify = async (guestId, email, assignedBy, clubId = null) => {
  try {
    // Use assignEmailToGuest to perform validation and assignment
    // This ensures consistent validation logic
    const guest = await assignEmailToGuest(guestId, email, assignedBy, clubId);
    
    // Update updated_at timestamp
    const { data: updatedGuest, error: updateError } = await supabase
      .from('players')
      .update({
        updated_at: new Date().toISOString()
      })
      .eq('id', guestId)
      .select()
      .single();

    if (updateError) throw updateError;

    // The trigger will automatically create the merge notification
    // But we can also explicitly trigger it to ensure it happens
    const { data: user } = await supabase
      .from('players')
      .select('id, email')
      .eq('email', email.toLowerCase().trim())
      .eq('is_guest', false)
      .maybeSingle();

    if (user) {
      // Trigger notification check
      await supabase.rpc('check_and_create_guest_notifications', {
        p_user_id: user.id,
        p_email: user.email
      });
    }

    return {
      status: 'assigned',
      guest: guest,
      notification_triggered: !!user
    };
  } catch (error) {
    console.error('[GuestLinkingService] Error assigning email and notifying:', error);
    throw error;
  }
};

/**
 * Assign an email to a guest and merge into an existing club member who has no games at this club.
 * @param {Object} params
 * @param {string} params.guestId
 * @param {string} params.email
 * @param {string} params.assignedBy
 * @param {string} params.clubId
 * @param {string} params.memberId
 * @returns {Promise<Object>}
 */
export const assignEmailAndMergeExistingMember = async ({
  guestId,
  email,
  assignedBy,
  clubId,
  memberId,
}) => {
  if (!guestId || !email || !assignedBy || !clubId || !memberId) {
    throw new Error('Missing data to merge guest into member');
  }

  const normalizedEmail = email.toLowerCase().trim();

  const { data: member, error: memberError } = await supabase
    .from('players')
    .select('id, email, first_name, last_name, is_guest')
    .eq('id', memberId)
    .eq('is_guest', false)
    .maybeSingle();

  if (memberError) throw memberError;
  if (!member) {
    throw new Error('Member account not found');
  }
  if (normalizeEmail(member.email) !== normalizeEmail(normalizedEmail)) {
    throw new Error('Email does not belong to this member');
  }

  const { data: membership, error: membershipError } = await supabase
    .from('players_club')
    .select('player_id')
    .eq('player_id', memberId)
    .eq('club_id', clubId)
    .eq('is_active', true)
    .maybeSingle();

  if (membershipError) throw membershipError;
  if (!membership) {
    throw new Error('This player is no longer an active member of this club.');
  }

  const clubGameCount = await countPlayerClubGames(memberId, clubId);
  if (clubGameCount > 0) {
    throw new Error(
      `This member has ${clubGameCount} game${clubGameCount === 1 ? '' : 's'} at this club. Guest history cannot be merged.`
    );
  }

  const { data: guestRow, error: guestError } = await supabase
    .from('players')
    .select('id, guest_invited_email')
    .eq('id', guestId)
    .eq('is_guest', true)
    .maybeSingle();

  if (guestError) throw guestError;
  if (!guestRow) {
    throw new Error('Guest account not found');
  }

  let guest = guestRow;
  const alreadyAssigned =
    normalizeEmail(guestRow.guest_invited_email) === normalizeEmail(normalizedEmail);

  if (!alreadyAssigned) {
    guest = await assignEmailToGuest(guestId, normalizedEmail, assignedBy, clubId, {
      allowExistingMemberWithoutGames: true,
      skipMergeNotification: true,
    });
  }

  const mergeResult = await mergeGuestAccountToUser(guestId, memberId);

  return {
    status: mergeResult?.status || 'merged',
    guest,
    merge: mergeResult,
  };
};

